import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useNgoProjectsQuery, useNgoProfileQuery, useNgoPortalsQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { Suspense, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, SearchIcon } from 'lucide-react';
import type { FundingType, ProjectStatus } from '@worldcoo/monolith/core';
import { Button } from '@worldcoo/ui/components/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@worldcoo/ui/components/select';
import { DonatingIllustration } from '@worldcoo/ui/custom/donating-illustration';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';
import { ProjectItemCard } from './-components/project-item-card';

const ITEMS_PER_PAGE = 10;

export const Route = createFileRoute('/ngos/projects/')({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		fundingType: v.fallback(v.optional(v.picklist(['myPortal', 'worldcoo', 'both'])), undefined),
		status: v.fallback(v.optional(v.picklist(['draft', 'active', 'paused', 'funded'])), undefined),
		page: v.fallback(v.optional(v.number()), undefined),
	}),
	loaderDeps: ({ search: { q, fundingType, status } }) => ({ q, fundingType, status }),
	loader: ({ context, deps }) => {
		context.queryClient.ensureQueryData(trpc.ngo.getProfile.queryOptions());
		context.queryClient.ensureQueryData(
			trpc.ngo.getProjects.queryOptions({ query: deps.q, fundingType: deps.fundingType, status: deps.status }),
		);
		context.queryClient.ensureQueryData(trpc.ngo.getPortals.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<Suspense fallback={<ProjectsListSkeleton />}>
			<ProjectsList />
		</Suspense>
	);
}

function ProjectsList() {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, fundingType, status, page } = Route.useSearch();
	const ngo = useNgoProfileQuery();
	const isNgoDraft = 'isDraft' in ngo && ngo.isDraft;
	const data = useNgoProjectsQuery({ q, fundingType, status });
	const portalsData = useNgoPortalsQuery();
	const ngoPortalId = 'portals' in portalsData ? portalsData.portals[0]?.id : undefined;
	const [searchValue, setSearchValue] = useState(q ?? '');
	const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => () => clearTimeout(debounceRef.current), []);

	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			navigate({
				search: { q: value || undefined, fundingType, status, page: undefined },
			});
		}, 300);
	};

	const hasNoFilters = !q && !fundingType && !status;
	const isEmpty = data.projects.length === 0 && hasNoFilters;

	const currentPage = page ?? 1;
	const totalPages = Math.ceil(data.projects.length / ITEMS_PER_PAGE);
	const paginatedProjects = data.projects.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

	if (isEmpty) {
		return (
			<>
				<MainSectionHeader
					title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Projects`}</h1>}
				/>
				<MainSectionContent className="items-center justify-center">
					<div className="flex flex-col items-center justify-center py-20">
						<DonatingIllustration className="mb-8 h-48 w-auto text-[#f28e41]" />
						<h2 className="text-foreground mb-2 text-xl font-semibold">{t`Welcome to Projects`}</h2>
						<p className="text-muted-foreground mb-8 max-w-md text-center text-sm">
							{t`You don't have any projects yet. Create your first project to start receiving donations and making an impact.`}
						</p>
						<Button size="lg" asChild>
							<Link to="/ngos/projects/new" className="gap-2">
								<PlusIcon className="size-5" />
								{t`Create First Project`}
							</Link>
						</Button>
					</div>
				</MainSectionContent>
			</>
		);
	}

	return (
		<>
			{/* Header */}
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Projects`}</h1>}
				actions={
					<Button size="xl" asChild>
						<Link to="/ngos/projects/new" className="gap-2">
							<PlusIcon className="size-5" />
							{t`New Project`}
						</Link>
					</Button>
				}
			/>

			<MainSectionContent>
				{/* Search & Filters */}
				<div className="flex items-center justify-between px-6 pt-4">
					<div className="border-border flex h-[45px] w-[291px] items-center gap-2 rounded-xl border bg-white px-4">
						<SearchIcon className="text-muted-foreground size-4" />
						<input
							type="text"
							placeholder={t`Search projects by name`}
							value={searchValue}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="text-foreground placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
						/>
					</div>

					<div className="flex items-center gap-4">
						<span className="text-foreground text-base font-medium tracking-[-0.2px]">{t`Filters`}</span>
						<div className="flex gap-2">
							<FundingFilter current={fundingType} />
							<StatusFilter current={status} />
						</div>
					</div>
				</div>

				{/* Project List */}
				<div className="flex flex-col gap-4 p-6">
					<AnimatePresence initial={false} mode="popLayout">
						{paginatedProjects.map((project) => (
							<motion.div
								key={project.id}
								layout
								initial={{ opacity: 0, scale: 0.96 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.96 }}
								transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
							>
								<ProjectItemCard
									project={project}
									centerPadding="px-16"
									isNgoDraft={isNgoDraft}
									ngoPortalId={ngoPortalId}
								/>
							</motion.div>
						))}
					</AnimatePresence>

					{paginatedProjects.length === 0 && (
						<div className="text-muted-foreground flex h-32 items-center justify-center">{t`No projects found`}</div>
					)}
				</div>

				{/* Pagination */}
				{totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} />}
			</MainSectionContent>
		</>
	);
}

function Pagination(props: { currentPage: number; totalPages: number }) {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, fundingType, status } = Route.useSearch();

	return (
		<div className="flex items-center justify-between px-6 pb-6">
			<Button
				variant="outline-dark"
				size="xl"
				disabled={props.currentPage <= 1}
				onClick={() => navigate({ search: { q, fundingType, status, page: props.currentPage - 1 } })}
				className="disabled:opacity-0"
			>
				<ChevronLeftIcon className="size-5" />
				{t`Previous`}
			</Button>

			<div className="flex gap-2">
				{Array.from({ length: props.totalPages }, (_, i) => i + 1).map((pageNum) => (
					<Button
						key={pageNum}
						variant={pageNum === props.currentPage ? 'default' : 'outline'}
						size="sm"
						onClick={() => navigate({ search: { q, fundingType, status, page: pageNum } })}
						className="w-[37px]"
					>
						{pageNum}
					</Button>
				))}
			</div>

			<Button
				variant="outline-dark"
				size="xl"
				disabled={props.currentPage >= props.totalPages}
				onClick={() => navigate({ search: { q, fundingType, status, page: props.currentPage + 1 } })}
				className="disabled:opacity-0"
			>
				{t`Next`}
				<ChevronRightIcon className="size-5" />
			</Button>
		</div>
	);
}

function FundingFilter(props: { current: FundingType | undefined }) {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, status } = Route.useSearch();

	return (
		<Select
			value={props.current ?? '_all'}
			onValueChange={(val) => {
				navigate({
					search: { q, fundingType: val === '_all' ? undefined : (val as FundingType), status, page: undefined },
				});
			}}
		>
			<SelectTrigger className={props.current ? 'border-primary bg-primary/5' : ''}>
				<SelectValue placeholder={t`Available in`} />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="_all">{t`Available in`}</SelectItem>
				<SelectItem value="myPortal">{t`My Portal`}</SelectItem>
				<SelectItem value="worldcoo">{t`Worldcoo`}</SelectItem>
				<SelectItem value="both">{t`Both`}</SelectItem>
			</SelectContent>
		</Select>
	);
}

function StatusFilter(props: { current: ProjectStatus | undefined }) {
	const { t } = useLingui();
	const navigate = useNavigate({ from: '/ngos/projects/' });
	const { q, fundingType } = Route.useSearch();

	return (
		<Select
			value={props.current ?? '_all'}
			onValueChange={(val) => {
				navigate({
					search: { q, fundingType, status: val === '_all' ? undefined : (val as ProjectStatus), page: undefined },
				});
			}}
		>
			<SelectTrigger className={props.current ? 'border-primary bg-primary/5' : ''}>
				<SelectValue placeholder={t`Status`} />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="_all">{t`All statuses`}</SelectItem>
				<SelectItem value="draft">{t`Draft`}</SelectItem>
				<SelectItem value="active">{t`Active`}</SelectItem>
				<SelectItem value="paused">{t`Paused`}</SelectItem>
				<SelectItem value="funded">{t`Funded`}</SelectItem>
			</SelectContent>
		</Select>
	);
}

function ProjectsListSkeleton() {
	return (
		<div className="flex flex-1 flex-col">
			<div className="border-portal-border flex min-h-24 shrink-0 items-center justify-between border-b bg-white px-6 py-4">
				<Skeleton className="h-8 w-32" />
				<Skeleton className="h-12 w-40 rounded-xl" />
			</div>
			<div className="flex items-center justify-between px-6 pt-4">
				<Skeleton className="h-[45px] w-[291px] rounded-xl" />
				<div className="flex gap-2">
					<Skeleton className="h-[45px] w-[115px] rounded-xl" />
					<Skeleton className="h-[45px] w-[115px] rounded-xl" />
				</div>
			</div>
			<div className="flex flex-col gap-4 p-6">
				{Array.from({ length: 6 }).map((_, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<Skeleton key={`skeleton-${i}`} className="h-[128px] rounded-2xl" />
				))}
			</div>
		</div>
	);
}
